import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import api from '../api/axiosConfig';

import { ROLE_PERMISSION_PRESETS } from '../config/permissions';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// Worker self-service permission scopes
const WORKER_SCOPES = ['attendance.self', 'payslip.self', 'leave.self', 'site.view'];

/**
 * Accurately resolve permissions from user object with preset fallback
 */
function resolveRolePermissions(user) {
  const role = (user?.role || '').toUpperCase().replace(/\s+/g, '_');
  if (['SUPER_ADMIN', 'ADMIN'].includes(role)) return ['ALL'];
  if (['CUSTOMER', 'CLIENT'].includes(role)) {
    const existing = Array.isArray(user?.permissions) ? user.permissions : [];
    return Array.from(new Set([...existing, ...ROLE_PERMISSION_PRESETS.CUSTOMER]));
  }
  if (user?.permissions && Array.isArray(user.permissions) && user.permissions.length > 0) {
    return user.permissions;
  }
  if (role === 'WORKER') return ROLE_PERMISSION_PRESETS.WORKER;
  return ROLE_PERMISSION_PRESETS[role] || ['dashboard.view'];
}

/**
 * Determine the home route after login based on the user's role and permissions:
 * - Super Admin / Admin       → /admin/dashboard
 * - Customer / Client         → /admin/customer-portal
 * - Worker (self-service)     → /admin/workforce/my-payslips
 * - Anyone else               → /admin/dashboard
 */
function getHomeRoute(user, permissions = []) {
  if (!user) return '/login';
  if (permissions.includes('ALL') || ['Super Admin', 'Admin', 'admin'].includes(user.role)) {
    return '/admin/dashboard';
  }

  // Customer / Client
  const roleName = (user.role || '').toLowerCase();
  if (roleName === 'customer' || roleName === 'client' || permissions.includes('customer.portal')) {
    return '/admin/customer-portal';
  }

  // Worker: only has self-service scopes
  const isWorkerOnly = roleName === 'worker' || permissions.every(p =>
    WORKER_SCOPES.includes(p) || p === 'workforce.view'
  );
  if (isWorkerOnly) return '/admin/workforce/my-payslips';

  const hasAdminAccess = permissions.some(p =>
    ['dashboard.view', 'project.view', 'user.view', 'user.manage'].includes(p)
  );
  if (hasAdminAccess) return '/admin/dashboard';

  return '/admin/dashboard';
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Initialize Auth from storage or incoming URL SSO on app load
  useEffect(() => {
    const initAuth = () => {
      // 🟢 Check if incoming URL has SSO token (?token=...&user=...)
      if (typeof window !== 'undefined' && window.location.search) {
        const params = new URLSearchParams(window.location.search);
        const urlToken = params.get('token');
        const urlUser = params.get('user');
        if (urlToken && !urlToken.startsWith('prototype-jwt-token')) {
          localStorage.setItem('token', urlToken);
          let parsedUser = null;
          if (urlUser) {
            try {
              parsedUser = JSON.parse(urlUser);
            } catch {
              try {
                parsedUser = JSON.parse(decodeURIComponent(urlUser));
              } catch (e) {
                console.warn("Auth init URL user parse error:", e);
              }
            }
          }
          if (!parsedUser && urlToken) {
            try {
              const base64Url = urlToken.split('.')[1];
              if (base64Url) {
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const jsonPayload = decodeURIComponent(
                  atob(base64)
                    .split('')
                    .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join('')
                );
                parsedUser = JSON.parse(jsonPayload);
              }
            } catch (err) {
              console.warn("Could not extract user from token:", err);
            }
          }
          if (parsedUser) {
            localStorage.setItem('user', JSON.stringify(parsedUser));
            localStorage.setItem('briq_last_login', new Date().toISOString());
            setUser(parsedUser);
            const perms = resolveRolePermissions(parsedUser);
            setPermissions(perms);
            setLoading(false);
            return;
          }
        }
      }

      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('token');

      if (storedUser && storedToken && storedToken !== 'undefined' && storedToken !== 'null') {
        try {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
          const perms = resolveRolePermissions(parsedUser);
          setPermissions(perms);
        } catch (error) {
          console.error('Auth initialization failed:', error);
          localStorage.removeItem('user');
          localStorage.removeItem('token');
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password, rememberMe = false) => {
    try {
      const response = await api.post('/auth/login', { email, password, rememberMe });
      const raw = response.data;
      const payload = raw?.data !== undefined ? raw.data : raw;
      const actualUser = payload?.user || payload;
      const token = payload?.token || raw?.token;

      const userToStore = {
        ...actualUser,
        token: token,
      };
      
      if (token) {
        localStorage.setItem('token', token);
      }
      localStorage.setItem('user', JSON.stringify(userToStore));
      localStorage.setItem('briq_last_login', new Date().toISOString());

      if (rememberMe) {
        localStorage.setItem('briq_remember_me', 'true');
        localStorage.setItem('briq_remembered_email', email);
      } else {
        localStorage.removeItem('briq_remember_me');
        localStorage.removeItem('briq_remembered_email');
      }
      
      setUser(userToStore);
      const perms = resolveRolePermissions(userToStore);
      setPermissions(perms);
      
      // Return home route so LoginPage can navigate correctly per role
      const homeRoute = getHomeRoute(userToStore, perms);
      
      return { success: true, data: userToStore, homeRoute };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        message: error.response?.data?.error || error.response?.data?.message || 'Login failed. Please check your credentials.' 
      };
    }
  };

  const signup = async (userData) => {
    try {
      const response = await api.post('/auth/signup', userData);
      const raw = response.data;
      const payload = raw?.data !== undefined ? raw.data : raw;
      const actualUser = payload?.user || payload;
      const token = payload?.token || raw?.token;

      const userToStore = {
        ...actualUser,
        token: token,
      };

      if (token) {
        localStorage.setItem('token', token);
      }
      localStorage.setItem('user', JSON.stringify(userToStore));

      if (userData.rememberMe) {
        localStorage.setItem('briq_remember_me', 'true');
        localStorage.setItem('briq_remembered_email', userData.email);
      }

      setUser(userToStore);
      const perms = resolveRolePermissions(userToStore);
      setPermissions(perms);

      const homeRoute = getHomeRoute(userToStore, perms);
      return { success: true, data: userToStore, homeRoute };
    } catch (error) {
      console.error('Signup error:', error);
      return {
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || 'Registration failed. Please try again.'
      };
    }
  };

  const forgotPassword = async (email) => {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return {
        success: true,
        data: response.data,
        message: response.data?.message || 'Password reset instructions have been sent.',
        resetToken: response.data?.resetToken,
        devCode: response.data?.devCode
      };
    } catch (error) {
      console.error('Forgot password error:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.response?.data?.error || 'Failed to send reset code. Please check your email.'
      };
    }
  };

  const verifyResetCode = async (email, code, token) => {
    try {
      const response = await api.post('/auth/verify-reset-code', { email, code, token });
      return {
        success: true,
        data: response.data,
        message: response.data?.message || 'Verification code confirmed.',
        resetToken: response.data?.resetToken || token
      };
    } catch (error) {
      console.error('Verify reset code error:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.response?.data?.error || 'Invalid or expired verification code.'
      };
    }
  };

  const resetPassword = async (email, code, token, newPassword) => {
    try {
      const response = await api.post('/auth/reset-password', { email, code, token, newPassword });
      const raw = response.data;
      const actualUser = raw?.user || raw?.data?.user;
      const authToken = raw?.token || raw?.data?.token;

      if (authToken && actualUser) {
        localStorage.setItem('token', authToken);
        localStorage.setItem('user', JSON.stringify(actualUser));
        localStorage.setItem('briq_last_login', new Date().toISOString());
        setUser(actualUser);
        const perms = resolveRolePermissions(actualUser);
        setPermissions(perms);
        const homeRoute = getHomeRoute(actualUser, perms);
        return { success: true, message: raw?.message || 'Password reset successfully!', homeRoute };
      }

      return { success: true, message: raw?.message || 'Password reset successfully!' };
    } catch (error) {
      console.error('Reset password error:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.response?.data?.error || 'Failed to reset password. Please try again.'
      };
    }
  };

  const socialLogin = async ({ provider, email, name, avatar, role = 'Super Admin' }) => {
    try {
      const response = await api.post('/auth/social-login', {
        provider,
        email,
        name,
        avatar,
        role
      });
      const raw = response.data;
      const payload = raw?.data !== undefined ? raw.data : raw;
      const actualUser = payload?.user || payload;
      const token = payload?.token || raw?.token;

      const userToStore = {
        ...actualUser,
        token: token,
      };

      if (token) {
        localStorage.setItem('token', token);
      }
      localStorage.setItem('user', JSON.stringify(userToStore));
      localStorage.setItem('briq_last_login', new Date().toISOString());

      setUser(userToStore);
      const perms = resolveRolePermissions(userToStore);
      setPermissions(perms);

      const homeRoute = raw?.homeRoute || getHomeRoute(userToStore, perms);
      return { success: true, data: userToStore, homeRoute };
    } catch (error) {
      console.error('Social login error:', error);
      return {
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || `Sign in with ${provider} failed.`
      };
    }
  };

  const logout = () => {
    document.documentElement.classList.remove('dark');
    setUser(null);
    setPermissions([]);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  /**
   * checkAccess(permission):
   *  - null / undefined  → always true (public item)
   *  - 'ALL'             → Super Admin wildcard
   *  - exact scope match → permission exists in user's list
   */
  const checkAccess = useCallback((permission) => {
    if (!permission) return true;
    if (!user) return false;
    if (permissions.includes('ALL')) return true;
    return Array.isArray(permissions) && permissions.includes(permission);
  }, [user, permissions]);

  const value = {
    user,
    permissions,
    loading,
    login,
    signup,
    forgotPassword,
    verifyResetCode,
    resetPassword,
    socialLogin,
    logout,
    checkAccess,
    isAuthenticated: !!user,
    homeRoute: getHomeRoute(user, permissions),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;